// src/components/YouTubeEmbed.jsx
//
// Task 1 (Slice 3): lazy, privacy-friendly YouTube embed for the Home hero.
// Click-to-load: renders a thumbnail + play badge first (no iframe, so first paint
// stays fast and no youtube.com cookies are set), then injects a youtube-nocookie
// iframe only after the visitor clicks Play. Accepts a full watch / youtu.be / embed
// URL or a bare 11-char video id. Renders NOTHING (null) when the input is empty or
// cannot be parsed, so an unset admin field leaves no broken/empty box in the hero.
import React, { useState } from 'react';

const ID_RE = /^[A-Za-z0-9_-]{11}$/;

// slice4-fix-c: book-plate corner ornament. Stroke-only open-book line-art that inherits
// the per-mode frame accent via currentColor (champagne / terracotta / wood -- set on the
// .btl-video-frame wrapper in index.css). Decorative only -> aria-hidden + pointer-events-none.
function BookCorner({ className = '' }) {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true"
            className={`btl-video-corner absolute w-6 h-6 z-20 pointer-events-none ${className}`}
            fill="none" stroke="currentColor" strokeWidth="1.3"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 6.5C9.6 5 5.8 5 3.8 6V18c2-1 5.8-1 8.2 0.5 2.4-1.5 6.2-1.5 8.2-0.5V6c-2-1-5.8-1-8.2 0.5Z" />
            <path d="M12 6.5V19" />
        </svg>
    );
}

// Pull an 11-char video id out of the common YouTube URL shapes, or accept a bare id.
// Pure + defensive: regex-only (no URL constructor) so malformed input can never throw;
// returns null when nothing valid is found.
export function parseYouTubeId(input) {
    if (typeof input !== 'string') return null;
    const raw = input.trim();
    if (!raw) return null;
    if (ID_RE.test(raw)) return raw;                         // bare id
    const patterns = [
        /[?&]v=([A-Za-z0-9_-]{11})/,        // youtube.com/watch?v=ID
        /youtu\.be\/([A-Za-z0-9_-]{11})/,   // youtu.be/ID
        /\/embed\/([A-Za-z0-9_-]{11})/,     // youtube(-nocookie).com/embed/ID
        /\/shorts\/([A-Za-z0-9_-]{11})/     // youtube.com/shorts/ID
    ];
    for (const re of patterns) {
        const m = raw.match(re);
        if (m) return m[1];
    }
    return null;
}

// A missing maxres/sd thumbnail must fall back once, not reselect the broken
// responsive candidate. Keep the player URL and click-to-load behavior separate.
export function handleYouTubePosterError(event, id) {
    const img = event.currentTarget;
    if (img.hasAttribute('srcset')) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
    const fallback = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    if (img.getAttribute('src') !== fallback) img.setAttribute('src', fallback);
}

export default function YouTubeEmbed({ url, title = 'Intro video', className = '', style,
    posterSizes, priority = false, posterLoading = 'eager', poster }) {
    const [playing, setPlaying] = useState(false);
    const id = parseYouTubeId(url);
    if (!id) return null; // empty / unparseable -> render nothing (no empty box)

    // maxres isn't generated for every upload; fall back to hqdefault (always present).
    const thumb = poster || `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
    // Opt-in only on homepage surfaces: existing lesson posters keep their
    // current source/loading behavior. Fixed 16:9 frame/crop is unchanged.
    const posterSrcSet = posterSizes && !poster ? [
        ['mqdefault', 320], ['hqdefault', 480], ['sddefault', 640], ['maxresdefault', 1280]
    ].map(([name, width]) => `https://i.ytimg.com/vi/${id}/${name}.jpg ${width}w`).join(', ') : undefined;

    return (
        <div style={style}
            className={`btl-video-frame relative w-full aspect-[16/9] overflow-hidden border border-book-accent/30 bg-book-pageDark shadow-gold-glow text-book-accent ${className}`}>
            {playing ? (
                <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
                    title={title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            ) : (
                <button type="button" onClick={() => setPlaying(true)} aria-label={`Play ${title}`}
                    className="group absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer">
                    <img src={thumb} srcSet={posterSrcSet} sizes={posterSizes} alt="" aria-hidden="true"
                        width="1280" height="720" loading={posterLoading} decoding="async"
                        fetchpriority={priority ? 'high' : 'auto'}
                        onError={(e) => handleYouTubePosterError(e, id)}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                    {/* Gold-to-dark wash for contrast behind the play badge */}
                    <span className="absolute inset-0 bg-gradient-to-t from-book-bg/80 via-book-bg/10 to-transparent" />
                    <span className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-book-accent/90 text-black shadow-gold-glow group-hover:bg-book-accentLight group-hover:scale-105 transition-all duration-300">
                        <svg viewBox="0 0 24 24" className="w-7 h-7 ml-1" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                </button>
            )}
            {/* slice4-fix-c: four book-plate corner ornaments framing the embed; colour follows
                the per-mode frame accent via currentColor. Overlay both states; decorative only. */}
            <BookCorner className="top-1.5 left-1.5" />
            <BookCorner className="top-1.5 right-1.5" />
            <BookCorner className="bottom-1.5 left-1.5" />
            <BookCorner className="bottom-1.5 right-1.5" />
        </div>
    );
}
